import {
  ChevronDown,
  ChevronRight,
  Users,
  GraduationCap,
  CreditCard,
  MessageCircle,
  Settings,
  HelpCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'wouter';

import { PublicShell } from '@/components/public-shell';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  // Learner Questions
  {
    id: 'enrollment-process',
    category: 'learners',
    question: 'How do I enroll in a class?',
    answer:
      "Browse classes, choose the session you want, and continue through secure checkout. After your booking is confirmed, you'll see the enrollment in your account and receive the session details by email.",
  },
  {
    id: 'class-access',
    category: 'learners',
    question: 'How long do I have access to a class after purchasing?',
    answer:
      'Your Kalovate account keeps the booking details for your enrollment. Any follow-up materials, recordings, or downloadable resources depend on the specific class and will be described on that class page.',
  },
  {
    id: 'refund-policy',
    category: 'learners',
    question: 'What is your refund policy?',
    answer:
      "Each class lists its own cancellation policy. Eligible refund requests are automatically approved up to that policy's cutoff; requests outside the automatic window may go to creator or admin review. Kalovate does not apply a blanket 30-day money-back guarantee across all classes.",
  },
  {
    id: 'community-access',
    category: 'learners',
    question: 'How do I access the community forum?',
    answer:
      "The community forum is automatically accessible once you're enrolled in at least one class. Simply log in to your account and navigate to the 'Community' section. You can participate in general discussions or class-specific topics based on your enrollments.",
  },
  {
    id: 'technical-requirements',
    category: 'learners',
    question: 'What are the technical requirements for taking classes?',
    answer:
      'Our platform works on modern mobile and desktop browsers with an internet connection. For the best experience, we recommend a stable connection for video streaming and checkout.',
  },
  {
    id: 'certificates',
    category: 'learners',
    question: 'Do you provide certificates of completion?',
    answer:
      'Not currently. If a creator includes follow-up materials or participation notes, that will be explained in the class listing or shared after the session.',
  },
  {
    id: 'creator-support',
    category: 'learners',
    question: 'How can I support my favorite creators?',
    answer:
      'You can support creators by enrolling in their classes, leaving positive reviews, and sending direct tips through their profile pages.',
  },

  // Creator Questions
  {
    id: 'become-creator',
    category: 'creators',
    question: 'How do I become a Kalovate creator?',
    answer:
      "Visit the 'Create with Kalovate' page and fill out the application form with details about your expertise, experience, and teaching goals. Our team reviews applications and looks for creators who can provide real value to their communities.",
  },
  {
    id: 'revenue-share',
    category: 'creators',
    question: 'What percentage of revenue do creators keep?',
    answer:
      'Creators keep up to 90% of all class sales revenue. This is one of the highest revenue shares in the industry. We believe in fairly compensating creators for their expertise and hard work in developing quality educational content.',
  },
  {
    id: 'curriculum-support',
    category: 'creators',
    question: 'Do you help with curriculum development?',
    answer:
      'Yes! We provide comprehensive support for curriculum development, including content structuring guidance, video production tips, and best practices for creating engaging online classes. Our creator success team works closely with you throughout the process.',
  },
  {
    id: 'marketing-help',
    category: 'creators',
    question: 'How do you help market my classes?',
    answer:
      'We can feature creator classes across Kalovate surfaces such as local discovery pages, newsletters, and editorial spots when they fit the market. We also help tighten class listings so they are easier to understand and book.',
  },
  {
    id: 'payment-schedule',
    category: 'creators',
    question: 'When and how do I receive payments?',
    answer:
      'Creator earnings are scheduled after each class session ends and are released through Stripe Connect once the payout is eligible. Exact timing depends on the active fee profile and whether any refund or account holds need review.',
  },
  {
    id: 'physical-space',
    category: 'creators',
    question: 'What physical classroom spaces are available?',
    answer:
      'In-person classes depend on the market, venue, and creator setup for that listing. When a class includes a physical location, the class page will show the delivery mode and location details before checkout.',
  },
  {
    id: 'class-pricing',
    category: 'creators',
    question: 'How do I price my classes?',
    answer:
      'You have full control over your class pricing. We provide market research and pricing guidance based on similar classes, your expertise level, and content depth. Our creator success team can help you find the optimal price point for your audience.',
  },

  // Technical Questions
  {
    id: 'account-issues',
    category: 'technical',
    question: "I'm having trouble logging in. What should I do?",
    answer:
      "First, try resetting your password using the 'Forgot Password' link on the login page. If you're still having issues, clear your browser cache and cookies, or try logging in from a different browser. Contact our support team if problems persist.",
  },
  {
    id: 'video-playback',
    category: 'technical',
    question: "Videos aren't playing properly. How can I fix this?",
    answer:
      'Ensure you have a stable internet connection and try refreshing the page. Videos work best on modern browsers like Chrome, Firefox, Safari, or Edge. If issues continue, try disabling browser extensions or switching to an incognito/private browsing window.',
  },
  {
    id: 'mobile-experience',
    category: 'technical',
    question: 'Is there a mobile app?',
    answer:
      'There is no native mobile app right now. Kalovate is designed to work in modern mobile browsers for browsing, booking, and managing your account.',
  },
  {
    id: 'download-materials',
    category: 'technical',
    question: 'Can I download class materials for offline access?',
    answer:
      "Yes! Most classes include downloadable materials like PDFs, templates, and resources that you can access offline. Video content is currently streaming-only, but we're working on offline video downloads for mobile apps.",
  },

  // General Questions
  {
    id: 'what-is-kalovate',
    category: 'general',
    question: 'What makes Kalovate different from other online learning platforms?',
    answer:
      'Kalovate focuses on connecting learners with passionate creators and experts who are actively practicing their craft. We emphasize community, creator support, and personal connections rather than just content consumption. Our revenue-sharing model and creator support services are designed to build sustainable careers for educators.',
  },
  {
    id: 'class-quality',
    category: 'general',
    question: 'How do you ensure class quality?',
    answer:
      'We carefully vet all creator applications and work closely with creators during content development. All classes go through a quality review process before publication. We also collect student feedback and ratings to continuously improve the learning experience.',
  },
  {
    id: 'free-content',
    category: 'general',
    question: 'Do you offer any free content?',
    answer:
      'While most classes are paid to support our creators, many creators offer free preview lessons or bonus content. We also regularly feature free community events, live Q&A sessions, and workshops for our members.',
  },
  {
    id: 'corporate-training',
    category: 'general',
    question: 'Do you offer corporate training or group discounts?',
    answer:
      'Yes! We offer custom programs and group enrollment options for teams and organizations. Reach out at admin@kalovate.com to discuss your needs.',
  },
];

const categories = [
  { id: 'learners', name: 'For Learners', icon: GraduationCap, color: 'bg-primary' },
  { id: 'creators', name: 'For Creators', icon: Users, color: 'bg-accent' },
  { id: 'technical', name: 'Technical Support', icon: Settings, color: 'bg-green-500' },
  { id: 'general', name: 'General', icon: HelpCircle, color: 'bg-blue-500' },
];

export default function FAQ() {
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const toggleItem = (itemId: string) => {
    setOpenItems(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const filteredFAQs =
    selectedCategory === 'all' ? faqData : faqData.filter(faq => faq.category === selectedCategory);

  return (
    <PublicShell routeKey="faq">
      {/* Header */}
      <section className="bg-muted py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-muted-foreground mb-8">
            Find answers to common questions about classes, creators, payments, and more.
          </p>

          <div className="flex justify-center">
            <Badge className="bg-primary/10 text-primary px-4 py-2">
              Can't find what you're looking for?{' '}
              <Link href="/contact" className="underline ml-1">
                Contact us
              </Link>
            </Badge>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-background border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedCategory('all')}
              className="flex items-center gap-2"
              data-testid="filter-all-categories"
            >
              All Questions
            </Button>

            {categories.map(category => {
              const Icon = category.icon;
              return (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? 'default' : 'outline'}
                  onClick={() => setSelectedCategory(category.id)}
                  className="flex items-center gap-2"
                  data-testid={`filter-category-${category.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {category.name}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {filteredFAQs.map(faq => {
              const isOpen = openItems.includes(faq.id);
              const category = categories.find(cat => cat.id === faq.category);

              return (
                <Card key={faq.id} className="overflow-hidden">
                  <Collapsible>
                    <CollapsibleTrigger
                      onClick={() => toggleItem(faq.id)}
                      className="w-full"
                      data-testid={`faq-question-${faq.id}`}
                    >
                      <CardHeader className="hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 text-left">
                            {category && (
                              <div
                                className={`w-6 h-6 ${category.color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}
                              >
                                <category.icon className="w-3 h-3 text-white" />
                              </div>
                            )}
                            <CardTitle className="text-lg font-semibold text-foreground">
                              {faq.question}
                            </CardTitle>
                          </div>
                          <div className="flex-shrink-0">
                            {isOpen ? (
                              <ChevronDown className="w-5 h-5 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <CardContent className="pt-0 pb-6">
                        <div className="ml-9 pr-9">
                          <p
                            className="text-muted-foreground leading-relaxed"
                            data-testid={`faq-answer-${faq.id}`}
                          >
                            {faq.answer}
                          </p>
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              );
            })}
          </div>

          {filteredFAQs.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🤔</div>
              <h3 className="text-2xl font-bold text-foreground mb-2">No Questions Found</h3>
              <p className="text-muted-foreground mb-6">
                No questions match the selected category.
              </p>
              <Button
                onClick={() => setSelectedCategory('all')}
                data-testid="button-show-all-questions"
              >
                Show All Questions
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Quick Access Cards */}
      <section className="py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">Need More Help?</h2>
            <p className="text-lg text-muted-foreground">
              We're here to support you every step of the way.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Contact Support</h3>
                <p className="text-muted-foreground mb-6">
                  Get personalized help from our friendly support team.
                </p>
                <Link href="/contact">
                  <Button
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="button-contact-support"
                  >
                    Contact Us
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Join Community</h3>
                <p className="text-muted-foreground mb-6">
                  Connect with other learners and get help from the community.
                </p>
                <Link href="/community">
                  <Button
                    variant="outline"
                    className="border-accent text-accent hover:bg-accent/10"
                    data-testid="button-join-community"
                  >
                    Community Forum
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Create with Kalovate</h3>
                <p className="text-muted-foreground mb-6">
                  Share your expertise and start earning as a Kalovate creator.
                </p>
                <Link href="/creator/apply">
                  <Button
                    variant="outline"
                    className="border-green-600 text-green-600 hover:bg-green-50"
                    data-testid="button-become-creator"
                  >
                    Apply Now
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Popular Topics */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-4">Most Asked Questions</h2>
            <p className="text-muted-foreground">Quick answers to our most popular questions.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-foreground mb-2">How do refunds work?</h3>
                <p className="text-sm text-muted-foreground">
                  Each class sets its own cancellation policy, and eligible requests are handled
                  against that policy.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-foreground mb-2">What do creators earn?</h3>
                <p className="text-sm text-muted-foreground">
                  Creators keep up to 90% of all revenue from their classes - one of the highest
                  rates in the industry.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-foreground mb-2">Is there lifetime access?</h3>
                <p className="text-sm text-muted-foreground">
                  Yes! Once you purchase a class, you have unlimited lifetime access to all content.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-foreground mb-2">How do I support creators?</h3>
                <p className="text-sm text-muted-foreground">
                  Enroll in their classes, leave reviews, and send tips directly through their
                  profile pages.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
